import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Plus, Check } from 'lucide-react';
import { api, COLOR_HEX } from '../lib/api';
import { logger } from '../lib/logger';
import { useQuoteCart } from '../context/QuoteCartContext';
import { useBuyer } from '../context/BuyerAuthContext';
import ReservedTerms from '../components/ReservedTerms';


export default function ProductDetailPage() {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const [product, setProduct] = useState(null);
  const [standardFeatures, setStandardFeatures] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const { add, items } = useQuoteCart();
  const { buyer } = useBuyer();

  useEffect(() => {
    api.get(`/products/${id}`)
      .then(r => setProduct(r.data))
      .catch(err => logger.warn('[ProductDetail] Failed to load product:', err?.message));
    api.get('/standard-features')
      .then(r => setStandardFeatures(r.data))
      .catch(err => logger.warn('[ProductDetail] Failed to load standard features:', err?.message));
  }, [id]);

  if (!product) {
    return <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-24 font-serif italic text-2xl text-[#3D2817]/50">Loading…</div>;
  }

  const lang = i18n.language;
  const tr = product.translations?.[lang] || product.translations?.en || product.translations?.it || {};
  const name = tr.name || product.sku;
  const description = tr.description || '';
  const standardText = standardFeatures?.translations?.[lang] || standardFeatures?.translations?.en || standardFeatures?.translations?.it || '';
  const inCart = items.some(i => i.id === product.id);

  return (
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-12 md:py-16">
      <Link to="/catalog" data-testid="back-to-catalog" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#3D2817]/60 hover:text-[#C5A059] mb-12 transition-colors">
        <ArrowLeft className="w-4 h-4" />
        {t('product.back')}
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {/* IMAGES */}
        <div className="lg:col-span-7">
          <div className="aspect-[4/5] bg-[#F2ECE0] overflow-hidden mb-4">
            {product.images?.[activeImg] ? (
              <img src={product.images[activeImg]} alt={name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <span className="font-serif text-8xl italic text-[#C5A059]/50">K.</span>
              </div>
            )}
          </div>
          {product.images?.length > 1 && (
            <div className="grid grid-cols-5 gap-3">
              {product.images.map((img, i) => (
                <button
                  key={img}
                  data-testid={`product-thumb-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-square overflow-hidden border-2 transition-all ${
                    activeImg === i ? 'border-[#C5A059]' : 'border-transparent hover:border-[#3D2817]/20'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* INFO */}
        <div className="lg:col-span-5">
          <div className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-3">— {product.sku}</div>
          <h1 className="font-serif font-light text-5xl md:text-6xl leading-none mb-4">{name}</h1>
          {tr.tagline && <p className="font-serif italic text-xl text-[#3D2817]/60 mb-8">{tr.tagline}</p>}

          {description && (
            <div className="mb-10 pb-10 border-b border-[#E5D9C8]">
              <div className="text-xs uppercase tracking-widest text-[#3D2817]/60 mb-3">{t('product.description')}</div>
              <p className="text-base text-[#3D2817]/80 leading-relaxed whitespace-pre-wrap">{description}</p>
            </div>
          )}

          {/* SPECS */}
          <dl className="space-y-4 mb-10">
            {product.formats?.length > 0 && (
              <div className="flex justify-between items-center py-3 border-b border-[#E5D9C8]">
                <dt className="text-xs uppercase tracking-widest text-[#3D2817]/60">{t('product.formats')}</dt>
                <dd className="font-sans text-sm">{product.formats.map(f => t(`formats.${f}`)).join(' · ')}</dd>
              </div>
            )}
            {product.colors?.length > 0 && (
              <div className="flex justify-between items-center py-3 border-b border-[#E5D9C8]">
                <dt className="text-xs uppercase tracking-widest text-[#3D2817]/60">{t('product.colors')}</dt>
                <dd className="flex gap-2">
                  {product.colors.map(c => (
                    <span key={c} className="w-5 h-5 rounded-full border border-[#E5D9C8]" title={t(`colors.${c}`)} style={{ background: COLOR_HEX[c] }} />
                  ))}
                </dd>
              </div>
            )}
            {(product.micron_min != null || product.micron_max != null || product.micron != null) && (
              <div className="flex justify-between items-center py-3 border-b border-[#E5D9C8]">
                <dt className="text-xs uppercase tracking-widest text-[#3D2817]/60">Micron</dt>
                <dd className="font-sans text-sm">{
                  (product.micron_min != null && product.micron_max != null)
                    ? `${product.micron_min}–${product.micron_max} µ`
                    : (product.micron_max != null ? `${product.micron_max} µ`
                    : (product.micron_min != null ? `${product.micron_min} µ`
                    : `${product.micron} µ`))
                }</dd>
              </div>
            )}
            {(product.melting_temp_min_c != null || product.melting_temp_max_c != null || product.melting_temp_c != null) && (
              <div className="flex justify-between items-center py-3 border-b border-[#E5D9C8]">
                <dt className="text-xs uppercase tracking-widest text-[#3D2817]/60">{t('product.melting_temp')}</dt>
                <dd className="font-sans text-sm">{
                  (product.melting_temp_min_c != null && product.melting_temp_max_c != null)
                    ? `${product.melting_temp_min_c}–${product.melting_temp_max_c} °C`
                    : `${product.melting_temp_max_c ?? product.melting_temp_min_c ?? product.melting_temp_c} °C`
                }</dd>
              </div>
            )}
            {(product.working_temp_min_c != null || product.working_temp_max_c != null || product.working_temp_c != null) && (
              <div className="flex justify-between items-center py-3 border-b border-[#E5D9C8]">
                <dt className="text-xs uppercase tracking-widest text-[#3D2817]/60">{t('product.working_temp')}</dt>
                <dd className="font-sans text-sm">{
                  (product.working_temp_min_c != null && product.working_temp_max_c != null)
                    ? `${product.working_temp_min_c}–${product.working_temp_max_c} °C`
                    : `${product.working_temp_max_c ?? product.working_temp_min_c ?? product.working_temp_c} °C`
                }</dd>
              </div>
            )}
            {product.viscosity_cp != null && (
              <div className="flex justify-between items-center py-3 border-b border-[#E5D9C8]">
                <dt className="text-xs uppercase tracking-widest text-[#3D2817]/60">{t('product.viscosity')}</dt>
                <dd className="font-sans text-sm">{product.viscosity_cp.toLocaleString()} cP{product.viscosity_temp_c != null && ` @ ${product.viscosity_temp_c} °C`}</dd>
              </div>
            )}
            {(product.package_weight_kg != null || product.package_pieces != null) && (
              <div className="flex justify-between items-center py-3 border-b border-[#E5D9C8]">
                <dt className="text-xs uppercase tracking-widest text-[#3D2817]/60">{t('product.package')}</dt>
                <dd className="font-sans text-sm">
                  {product.package_weight_kg != null && `${product.package_weight_kg} kg`}
                  {product.package_pieces != null && ` · ${product.package_pieces} pcs`}
                </dd>
              </div>
            )}
            {product.moq != null && (
              <div className="flex justify-between items-center py-3 border-b border-[#E5D9C8]">
                <dt className="text-xs uppercase tracking-widest text-[#3D2817]/60">{t('product.moq')}</dt>
                <dd className="font-sans text-sm">{product.moq}</dd>
              </div>
            )}
          </dl>

          <button
            data-testid="product-add-quote"
            onClick={() => add({ id: product.id, sku: product.sku, name })}
            disabled={inCart}
            className={`w-full py-5 text-sm uppercase tracking-[0.25em] transition-all duration-500 flex items-center justify-center gap-3 ${
              inCart ? 'bg-[#C5A059] text-white' : 'bg-[#3D2817] hover:bg-[#C5A059] text-white hover:-translate-y-0.5'
            }`}
          >
            {inCart ? <><Check className="w-4 h-4" /> Added to quote</> : <><Plus className="w-4 h-4" /> {t('catalog.request_quote')}</>}
          </button>

          {buyer?.product_notes?.[product.id] && (
            <div className="mt-6 p-4 bg-[#C5A059]/10 border-l-2 border-[#C5A059]">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] mb-1">— Your personal terms</div>
              <p className="text-sm text-[#3D2817]/80 whitespace-pre-wrap">{buyer.product_notes[product.id]}</p>
            </div>
          )}
          {buyer && <ReservedTerms terms={buyer.product_terms?.[product.id]} />}

          {standardText && (
            <div className="mt-12">
              <div className="text-xs uppercase tracking-widest text-[#C5A059] mb-3">{t('product.standard_features')}</div>
              <p className="text-sm text-[#3D2817]/70 leading-relaxed whitespace-pre-wrap">{standardText}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
